import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import Icon from "components/AppIcon";
import ClabLogoPng from "components/ui/ClabLogoPng";
import LoginForm from "./components/LoginForm";
import LanguageSelector from "./components/LanguageSelector";
import { getRoleName, getUserDashboardRoute } from "../../utils/roleUtils";

const LoginScreen = () => {
  const navigate = useNavigate();
  /* eslint-disable-next-line no-unused-vars */
  const { user, signOut } = useAuth();
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [hasRedirected, setHasRedirected] = useState(false);

  // Redirect if already logged in - but prevent multiple redirects
  useEffect(() => {
    if (user?.id && !hasRedirected) {
      // Strict Role Check for Main Login Page (CLAB/Admin only)
      const rawRole = user.role_name || user.role;
      let role = 'guest';

      if (typeof rawRole === 'string') {
        role = rawRole.toLowerCase();
      } else if (typeof rawRole === 'number') {
        const map = { 0: 'admin', 1: 'clab', 2: 'recruiter', 3: 'employer' };
        role = map[rawRole] || 'guest';
      }
      
      // Normalize
      if (role === 'clab_user') role = 'clab';
      if (role === 'master_admin') role = 'admin';

      const allowedRoles = ['admin', 'clab'];

      if (allowedRoles.includes(role)) {
        setHasRedirected(true);
        const dashboardRoute = getUserDashboardRoute(user.role);
        navigate(dashboardRoute, { replace: true });
      } else {
        // User is logged in but NOT authorized for this login portal. Force logout.
        console.warn(`Blocked unwanted login on Main Login Page: ${role}`);
        signOut();
      }
    }
  }, [user, hasRedirected, navigate, signOut]);

  return (
    <div className="min-h-screen w-full flex flex-col relative bg-[#0288d1] md:bg-[url('/assets/clab_login_bg.png')] md:bg-[length:100%_100%] md:bg-center md:bg-no-repeat">
      {/* Header - Empty for now as requested to remove dropdown */}
      <header className="relative z-10 w-full px-8 py-4 flex justify-end items-center">
         {/* Potential spot for FAQ/Manual links if needed later */}
      </header>

      {/* Main Content - Centered on Mobile, Aligned Right on Desktop */}
      <main className="flex-1 flex items-center justify-center md:justify-end px-4 md:px-16 lg:px-24 relative z-10 pb-4">
        <div className="w-full max-w-md">
          
          <div className="text-center mb-6">
             {/* CLAB Logo added above e-Biodata as requested */}
            <img 
              src="/assets/logo_clab_white.png" 
              alt="CLAB Logo" 
              className="h-20 mx-auto mb-2 object-contain"
            />
            <img 
              src="/assets/logo_ebiodata.png" 
              alt="eBiodata Logo" 
              className="h-12 mx-auto mb-2 object-contain drop-shadow-md"
            />
          </div>

          {/* Login Container - Transparent, no box */}
          <div className="p-4"> 
            <LoginForm
              selectedLanguage={selectedLanguage}
              mode="regular"
            />
            
             <div className="mt-4 text-center">
              <Link
                to="/employer-login"
                className="text-white hover:text-blue-200 underline text-sm font-bold block mb-2"
              >
                e-CLAB Employer Login
              </Link>
              <Link
                to="/recruiter-login"
                className="text-white/60 hover:text-white underline text-sm"
              >
                Not a Clab User? Go to Recruiter Login
              </Link>
            </div>
          </div>

          {/* Footer Info - Specific text provided - Hidden on Mobile */}
          <div className="mt-8 text-right text-xs text-white/90 font-medium leading-relaxed hidden md:block">
             <div className="flex justify-end items-center gap-4 mb-2">
                <span className="flex items-center gap-1"><Icon name="Phone" size={12} /> 03 2095 9599</span>
                <span className="flex items-center gap-1"><Icon name="MessageCircle" size={12} /> 012 694 9599</span>
                <span className="flex items-center gap-1"><Icon name="Mail" size={12} /> vdrclab@clab.com.my</span>
             </div>
             <p className="text-center w-full">Level 2, Annexe, Menara Milenium, No. 8, Jalan Damanlela Bukit Damansara,</p>
             <p className="text-center w-full">Damansara Town Centre, 50490 Kuala Lumpur.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginScreen;